import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  Spinner,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import usePersistStoreData from "../../Components/Hooks/usePersistStoreData";
import TableContainerSearch from "../../Components/Common/TableContainerSearch";
import TableContainer1 from "../../Components/Common/TableContainer1";
import axios from "axios";
import TableContainerDateRangeFilter from "../../Components/Common/TableContainerDateRangeFilter";
import exportToPDF from "../../Components/Common/exportToPDF";
import exportToExcel from "../../Components/Common/exportToExcel";
import { useDispatch } from "react-redux";
import { shareEmailReportThunk } from "../../slices/thunks";
import { showToast } from "../../Components/ToastNotification";
import CommonLoading from "../../Components/Common/CommonLoading";
import {
  adjustSelectedDateRange,
  getCurrentFiscalYearDates,
  getWorkingDateRange,
} from "../../Components/Common/WorkingDateRange";

const FinancialLedger = () => {
  // document.title = "Dashboard | B.N. Rathi";

  const { userData } = usePersistStoreData();
  const dispatch = useDispatch();

  const token = userData?.token;
  const [dateRange, setDateRange] = useState([]);
  const [rightColumn, setRightColumn] = useState(true);
  const toggleRightColumn = () => {
    setRightColumn(!rightColumn);
  };
  const [isTopPageDropdown, setTopPageDropdown] = useState(false);
  const toggleDropdown = () => {
    setTopPageDropdown(!isTopPageDropdown);
  };

  const columns = useMemo(
    () => [
      {
        id: "settlement_date",
        Header: "Sett. Date",
        accessorKey: "settlement_date",
      },
      { id: "exchange", Header: "Exchange", accessorKey: "exchange" },
      { id: "segment", Header: "Segment", accessorKey: "segment" },
      {
        id: "particulars",
        Header: "Particulars",
        accessorKey: "particulars",
        // cell: (cell) => {
        //   const row = cell.row.original;
        //   const isMtf = row.is_mtf;
        //   const value = cell.getValue();
        //   return (
        //     <span>
        //       {value}
        //       <span className="fs-20 fw-semibold text-danger">
        //         {" "}
        //         {isMtf ? " *" : ""}
        //       </span>
        //     </span>
        //   );
        // },
      },
      {
        id: "debit",
        Header: "Debit (₹)",
        accessorKey: "debit",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          // console.log("row", row);
          return (
            <>
              <span class="  mb-0">
                {row?.getValue()
                  ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                  : 0}{" "}
              </span>
            </>
          );
        },
      },
      {
        id: "credit",
        Header: "Credit (₹)",
        accessorKey: "credit",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          // console.log("row", row);
          return (
            <>
              <span class="  mb-0">
                {row?.getValue()
                  ? parseFloat(row?.getValue()).toLocaleString("en-IN")
                  : 0}{" "}
              </span>
            </>
          );
        },
      },

      {
        id: "running_balance",
        Header: "Balance (₹)",
        accessorKey: "running_balance",
        headerTextAlign: "end",
        bodyTextAlign: "end",
        cell: (row) => {
          const rawValue = row?.getValue();
          const value = parseFloat(rawValue);
          const formattedValue = !isNaN(value)
            ? value.toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : "0.00";
          return (
            <>
              {value > 0 ? (
                <span className="text-success mb-0">
                  {formattedValue}
                  {/* Optionally include icon if required */}
                </span>
              ) : value < 0 ? (
                <span className="text-danger mb-0">
                  {formattedValue}
                  {/* Optionally include icon if required */}
                </span>
              ) : (
                <span className="mb-0">{formattedValue}</span>
              )}
            </>
          );
        },
      },
    ],
    []
  );

  const [data, setData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exchangeFilter, setExchangeFilter] = useState("with-mtf");
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Get fiscal start and Today as end date
  const { fyStart } = getCurrentFiscalYearDates();
  const endDate = today;
  const [fromDate, setFromDate] = useState(fyStart);
  const [toDate, setToDate] = useState(endDate);

  const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month is 0-indexed
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handleShareEmail = async () => {
    let startDate, endDate;

    // If the user provided a date range, adjust those dates to working days.
    if (dateRange && dateRange.length === 2) {
      const { adjustedStart, adjustedEnd } = adjustSelectedDateRange(
        dateRange[0],
        dateRange[1]
      );
      startDate = formatDate(adjustedStart);
      endDate = formatDate(adjustedEnd);
    } else {
      const { fyStart } = getCurrentFiscalYearDates();
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);

      startDate = formatDate(fromDate);
      endDate = formatDate(toDate);
    }

    const body = {
      report_type: "ledger",
      "additional_data[start_date]": startDate,
      "additional_data[end_date]": endDate,
    };

    try {
      const response = await dispatch(
        shareEmailReportThunk({ body, token })
      ).unwrap();

      showToast("Report sent on your email successfully", "success");
    } catch (error) {
      console.error("Error submitting request:", error);
      showToast(
        error?.message || "Something went wrong, please try again",
        "error"
      );
    }
  };

  useEffect(() => {
    const fetchHoldings = async () => {
      setLoading(true);
      setError(null);

      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);

      // Get fiscal start and T-1 as end date
      const { fyStart } = getCurrentFiscalYearDates();
      const endDate = today;
      try {
        const response = await axios.get("financial-ledger", {
          params: {
            start_date: formatDate(fyStart),
            end_date: formatDate(endDate),
            type: "with-mtf",
          }, // Pass holdingType as query param
          headers: {
            Authorization: `Bearer ${userData?.token}`, // Add Authorization header
            "Content-Type": "application/json",
          },
        });

        setSummary(response?.data?.summary);
        // console.log("here", response, response.data?.summary);
        setData(response.data?.items); // Update state with fetched data
        // setTimeout(() => {
        setLoading(false);
        // }, 2000);
      } catch (err) {
        console.error("Error fetching holdings:", err);
        // setSummary(null)
        setError(err);
        setLoading(false);
      } finally {
        // setSummary(null)
        setLoading(false);
      }
    };

    fetchHoldings();
  }, []); // Runs when holdingType or token changes

  const handleDateRangeChange = async (values) => {
    // console.log("values",values)
    // setLoading(true);
    setError(null);

    // const holdingType = 1;

    try {
      const response = await axios.get("financial-ledger", {
        params: {
          start_date: values.start_date,
          end_date: values.end_date,
          type: exchangeFilter,
        },

        headers: {
          Authorization: `Bearer ${userData?.token}`, // Add Authorization header
          "Content-Type": "application/json",
        },
      });

      // console.log("here", response);
      setSummary(response.data?.summary);
      setData(response.data?.items);

      // Update state with fetched data
      // setTimeout(() => {
      values.setTableLoading(false);
      // }, 2000);
    } catch (err) {
      console.error("Error fetching holdings:", err);
      // setSummary(null)
      setError(err);
      values.setTableLoading(false);
    } finally {
      values.setTableLoading(false);
      // setSummary(null)
    }
  };

  // const tableHeaders = columns.map((col) => col.Header.replace(/₹/g, "Rs"));
  // const tableBody = data.map(
  //   (row) =>
  //     columns.map((col) =>
  //       row[col.accessorKey]
  //         ? typeof row[col.accessorKey] === "string"
  //           ? row[col.accessorKey]
  //           : row[col.accessorKey].toLocaleString("en-IN")
  //         : 0
  //     ) // Default to 0 if value is missing
  // );

  const tableHeaders = columns.map((col) => col.Header.replace(/₹/g, "Rs"));

  const tableBody = data.map((row) =>
    columns.map((col) =>
      row[col.accessorKey]
        ? typeof row[col.accessorKey] === "string"
          ? row[col.accessorKey]
          : row[col.accessorKey].toLocaleString("en-IN")
        : 0
    )
  );

  // Define which columns to sum
  const sumColumns = ["debit", "credit"];
  const apiTotals = {
    debit: summary?.total_debit,
    credit: summary?.total_credit,
  };

  // Calculate totals only for "debit" and "credit"
  const totals = columns.map((col, index) => {
    if (index === 0) return "TOTAL"; // First column will have "Total"

    return sumColumns.includes(col.accessorKey)
      ? apiTotals[col.accessorKey]?.toLocaleString("en-IN") || 0 // Use API total, default to 0 if missing
      : ""; // Leave other columns empty
  });

  // Add totals row to the table
  tableBody.push(totals);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {loading ? (
            <div
              className="centerContent h-100 w-100"
              style={{ minHeight: "80vh" }}
            >
              <CommonLoading />
            </div>
          ) : (
            <Card className="mb-0">
              <CardHeader>
                <div className="m-0 p-0 d-flex align-items-center justify-content-between">
                  <h3 className="m-0">Financial Ledger</h3>
                  <Dropdown
                    isOpen={isTopPageDropdown}
                    toggle={toggleDropdown}
                    className={`card-header-dropdown ${
                      data?.length === 0 ? "disabled-dropdown_export" : ""
                    }`}
                  >
                    <DropdownToggle
                      tag="a"
                      className="text-reset dropdown-btn"
                      role="button"
                      color="success"
                    >
                      <Button type="button" color="success">
                        Export<i className="ms-1 bx bx-caret-down"></i>
                      </Button>
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-end">
                      <DropdownItem
                        onClick={() =>
                          exportToPDF(
                            "Financial Ledger",
                            userData,
                            `Combined`,
                            `From: ${summary?.start} - To: ${summary?.end}`,
                            tableHeaders,
                            tableBody
                          )
                        }
                      >
                        Export as PDF
                      </DropdownItem>
                      <DropdownItem
                        onClick={() =>
                          exportToExcel(
                            "Financial Ledger",
                            userData,
                            `Combined`,
                            `From: ${summary?.start} - To: ${summary?.end}`,
                            tableHeaders,
                            tableBody,
                            "",
                            [],
                            []
                          )
                        }
                      >
                        Export Excel
                      </DropdownItem>
                      <DropdownItem onClick={handleShareEmail}>
                        Share on Email
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </CardHeader>
              <CardBody>
                <TableContainerDateRangeFilter
                  footer={false}
                  summary={summary}
                  columns={columns}
                  data={data}
                  onDateRangeChange={handleDateRangeChange}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  exchangeFilter={exchangeFilter}
                  setExchangeFilter={setExchangeFilter}
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                />
              </CardBody>
            </Card>
          )}
          {/* <BreadCrumb title="Dashboard" pageTitle="Dashboards" /> */}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default FinancialLedger;
